import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Copy, Send, CheckCircle2, Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_DISPLAY = {
  super_admin: "Super Admin",
  crm_admin: "CRM Admin",
  sales_manager: "Sales Manager",
  sales_agent: "Sales Representative",
  call_agent: "Call Center Agent",
  marketing_manager: "Marketing Manager",
  marketing_agent: "Marketing Agent",
  support: "Customer Success",
  read_only: "Viewer",
};

/**
 * MySignature — Settings → Account → My Signature (Phase 1 MVP).
 *
 * Matches the spec exactly: two-column layout, fixed template (editable
 * fields limited to name / title / phone), locked-fields notice, live
 * preview, Save + Copy + Send-test actions with spec-exact copy.
 *
 * Phase 2 (deferred) hooks: the global logo / website / social URLs come
 * from GET /api/signature/global — in Phase 2 a super_admin Signature
 * Manager will write those settings.
 */
export default function MySignature() {
  const token = useMemo(() => localStorage.getItem("token"), []);
  // P0-B (Apr 2026): admin-only env-var hints. Sales reps see a friendly
  // "ask your admin" CTA instead.
  const { user } = useAuth() || {};
  const isAdmin = !!(user && (user.role === "super_admin" || user.role === "crm_admin"));
  const [form, setForm] = useState({ full_name: "", job_title: "", phone_number: "", email_display: "", physical_address: "" });
  const [roleKey, setRoleKey] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [rendered, setRendered] = useState({ html: "", is_complete: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const loadRendered = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/signature/me/rendered`, { headers: { Authorization: `Bearer ${token}` } });
      setRendered({ html: r.data.html || "", is_complete: !!r.data.is_complete });
    } catch { /* noop */ }
  }, [token]);

  useEffect(() => {
    (async () => {
      try {
        const [sigRes, meRes] = await Promise.all([
          axios.get(`${API}/signature/me`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
        ]);
        setForm({
          full_name: sigRes.data.full_name || "",
          job_title: sigRes.data.job_title || "",
          phone_number: sigRes.data.phone_number || "",
          email_display: sigRes.data.email_display || "",
          physical_address: sigRes.data.physical_address || "",
        });
        setRoleKey(meRes?.data?.role || "");
        setUserEmail(meRes?.data?.email || "");
      } finally {
        await loadRendered();
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/signature/me`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Signature saved ✓");
      await loadRendered();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save signature");
    } finally { setSaving(false); }
  };

  const copy = async () => {
    if (!rendered.html) { toast.error("Nothing to copy — save your signature first"); return; }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(rendered.html);
      } else {
        const ta = document.createElement("textarea");
        ta.value = rendered.html;
        document.body.appendChild(ta); ta.select(); document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Signature copied to clipboard ✓");
    } catch {
      toast.error("Copy failed — select the preview manually");
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/signature/me/send-test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Test email sent to ${r.data.to} ✓`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 503 && detail?.code === "email_transport_not_configured") {
        toast.error(isAdmin
          ? "Email transport not configured. Connect Microsoft 365 in Settings → Integrations, or set SMTP env vars (SMTP_HOST / _USER / _PASSWORD)."
          : "Email isn't connected for your workspace yet. Ask your admin to enable Microsoft 365 or SMTP."
        );
      } else {
        toast.error(detail?.message || detail?.code || "Failed to send test email");
      }
    } finally { setSending(false); }
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const roleDisplay = ROLE_DISPLAY[roleKey] || (roleKey ? roleKey.replace(/_/g, " ") : "User");

  if (loading) {
    return <div data-testid="signature-loading" style={{ padding: 32, textAlign: "center" }}>
      <Loader2 className="animate-spin" style={{ width: 16, height: 16, color: "var(--text-tertiary)" }} />
    </div>;
  }

  return (
    <div data-testid="my-signature-page">
      <div style={{ fontSize: 20, fontWeight: 500, marginBottom: 4, color: "#111827" }}>My Email Signature</div>
      <div style={{ fontSize: 13, color: "#888888", marginBottom: 20 }}>
        Your signature is automatically added to emails sent from the CRM.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        {/* Left — form */}
        <div>
          <Field label="FULL NAME" helper="Pulled from your profile — editing here updates your signature only.">
            <input data-testid="signature-input-name" value={form.full_name} onChange={update("full_name")}
              className="input" style={{ fontSize: 13, width: "100%" }} />
          </Field>
          <Field label="JOB TITLE" helper={`Your CRM role is ${roleDisplay} — you can customize how your title displays in your signature.`}>
            <input data-testid="signature-input-title" value={form.job_title} onChange={update("job_title")}
              className="input" style={{ fontSize: 13, width: "100%" }} />
          </Field>
          <Field label="PHONE NUMBER" helper="Include your area code. Leave blank to hide the phone number from your signature.">
            <input data-testid="signature-input-phone" value={form.phone_number} onChange={update("phone_number")}
              placeholder="(XXX) XXX-XXXX"
              className="input" style={{ fontSize: 13, width: "100%" }} />
          </Field>
          {/* Iter 193 — Abigail #f925c608: contact email + physical
              company address fields. Physical address reduces spam
              scores when included in outbound emails (CAN-SPAM
              compliance). Both optional — blank fields drop out of the
              rendered signature. */}
          <Field label="CONTACT EMAIL" helper="Shown on your signature so leads can reply outside the CRM. Leave blank to hide.">
            <input data-testid="signature-input-email" value={form.email_display} onChange={update("email_display")}
              placeholder="you@enablehealthcare.com"
              className="input" style={{ fontSize: 13, width: "100%" }} />
          </Field>
          <Field label="COMPANY ADDRESS" helper="Physical mailing address. Including this in outbound emails lowers your spam score (CAN-SPAM best practice).">
            <textarea data-testid="signature-input-address" value={form.physical_address} onChange={update("physical_address")}
              placeholder="123 Main St, Suite 400&#10;New York, NY 10001"
              rows={2}
              className="input" style={{ fontSize: 13, width: "100%", fontFamily: "inherit", resize: "vertical" }} />
          </Field>

          <div data-testid="signature-locked-fields-notice"
            style={{ marginTop: 14, padding: 12, borderRadius: 8, border: "0.5px solid #E5E7EB", background: "#FAFAFA", display: "flex", gap: 8, alignItems: "flex-start" }}>
            <Lock style={{ width: 13, height: 13, color: "#6B7280", marginTop: 2 }} />
            <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.5 }}>
              The company logo, website link, and social media icons are managed by your admin and are consistent across all employee signatures.
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="signature-save-btn"
              style={{ fontSize: 13, padding: "8px 16px", background: "#2563eb", borderRadius: 8, color: "#fff", border: "none" }}>
              {saving ? <Loader2 className="animate-spin" style={{ width: 13, height: 13 }} /> : <Save style={{ width: 13, height: 13 }} />}
              Save signature
            </button>
          </div>
        </div>

        {/* Right — live preview */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>PREVIEW</div>
          <div data-testid="signature-preview" style={{ padding: 20, border: "0.5px solid #E5E7EB", borderRadius: 12, background: "#fff", minHeight: 180 }}
            dangerouslySetInnerHTML={{ __html: rendered.html || "<em style='color:#999;font-size:12px;'>Enter your details on the left to see a preview.</em>" }} />
          {rendered.is_complete && (
            <div data-testid="signature-complete-ok" style={{ marginTop: 8, fontSize: 11, color: "#256D37", display: "flex", alignItems: "center", gap: 4 }}>
              <CheckCircle2 style={{ width: 12, height: 12 }} /> Signature is complete and will auto-append to outbound emails.
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={copy} className="btn btn-ghost" data-testid="signature-copy-btn"
              style={{ fontSize: 13, padding: "8px 14px", border: "0.5px solid #D1D5DB", borderRadius: 8, background: "#fff" }}>
              <Copy style={{ width: 13, height: 13 }} /> Copy signature
            </button>
            <button onClick={sendTest} disabled={sending} className="btn btn-ghost" data-testid="signature-send-test-btn"
              style={{ fontSize: 13, padding: "8px 14px", border: "0.5px solid #D1D5DB", borderRadius: 8, background: "#fff" }}>
              {sending ? <Loader2 className="animate-spin" style={{ width: 13, height: 13 }} /> : <Send style={{ width: 13, height: 13 }} />}
              Send test email
            </button>
          </div>
          {userEmail && <div style={{ fontSize: 10, color: "#888", marginTop: 6 }}>Test email will go to <strong>{userEmail}</strong></div>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, helper, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</label>
      {children}
      {helper && <div style={{ fontSize: 11, color: "#888888", marginTop: 5 }}>{helper}</div>}
    </div>
  );
}
